import Vue from 'vue'
import decode from 'jwt-decode'
import { getUser, getRefreshToken, getAccessToken, setUser, setAccessToken, setRefreshToken, clearCredentials } from '@/utils/cookie'
import i18n from '@/i18n'
import securityStore from '../store/security-store.js'
import router from '@/router/index.js'
import { EventBus } from '@/router/bus'

// Claims
const CLAIMS = [
  'Organization_View', // 0
  'Organization_RegistrationResolve', // 1
  'Role_Manage', // 2
  'Role_View', // 3
  'User_Review', // 4
  'User_UpdateAll', // 5
  'User_StatusUpdate', // 6
  'User_StatusUpdateAll', // 7
  'User_View', // 8
  'User_Manage', // 9
  'Project_View', // 10
  'Project_Manage', // 11
  'Project_Evaluate', // 12
  'Project_ListView', // 13
  'Project_ListManage', // 14
  'Form_Manage', // 15
  'Form_ConfirmationResolve', // 16
  'Form_ApprovalResolve', // 17
  'DMS_Manage', // 18,
  'Organization_Manage', // 19
  'Form_ImportantManage', // 20
  'Project_Idea_Verification', // 21
  'Project_Idea_Approval', // 22,
  'Mei', // 23,
  'Minister', // 24,
  'Project_Send_Ministry', // 25
  'Project_Idea_Minister_Approval', // 26
  'Mei_Approval_Comment_Send', // 27,
  'Send_Request_Change_Form' // 28
]

const ADMIN_CLAIM = 'Admin'

// const AUTHORIZATION = 'Authorization'
// const REFRESH_TOKEN = 'Refresh_Token'

// Public methods

export function getClaims () {
  return CLAIMS
}

export function isUserActive () {
  return isAuth() && accessTokenValid()
}

// TODO Check this if it's valid
export function isAuth () {
  return !!getUser()
}

export function accessTokenValid () {
  var token = getAccessToken()
  return token && !isTokenExpired(token)
}

export function refreshTokenValid () {
  var token = getRefreshToken()
  return token && !isTokenExpired(token)
}

export function authorizedUserId () {
  var token = getAccessToken()
  return decode(token).user_id
}

export function authorizedUserName () {
  var token = getAccessToken()
  return decode(token).user_name
}

export function authorizedOrganizationId () {
  var token = getAccessToken()
  return decode(token).user_organization_id
}

export function login (user, accessToken, refreshToken) {
  const token = decode(accessToken)
  setUser(user)
  setAccessToken(accessToken)
  setRefreshToken(refreshToken)

  securityStore.dispatch('setIsAuth')
  securityStore.dispatch('startSessionInterval')
  securityStore.dispatch('setClaims', token.security_claims)
  securityStore.dispatch('setOrganizationId', token.user_organization_id)
  securityStore.dispatch('setUsername', user)
  securityStore.dispatch('setUserId', token.user_id)
  // let authorizationKey = Sha256(user, Vue.$config.secret_key)
  // Vue.http.headers.common['From'] = authorizationKey.toString()
  // console.log(Vue.http.headers)
  EventBus.$emit('logged-in', 'asd')
}

export function logout () {
  router.push('/').catch(() => { })
  securityStore.dispatch('resetState')
  clearCredentials()
  EventBus.$emit('logged-out', 'asd')
}

export function refreshToken (to, from, next) {
  var refreshTokenUrl = Vue.$apiConfig.loginRefresh
  const isProductionEnvironment = process.env.NODE_ENV.toUpperCase() === 'prod'.toUpperCase()

  if (!isProductionEnvironment) { console.log('Access token expired - sending refresh access token request from router') }

  Vue.http.headers.common.Authorization = 'Bearer ' + getRefreshToken()

  Vue.http.get(refreshTokenUrl)
    .then(response => {
      if (!isProductionEnvironment) { console.log('Access token obtained successfully - redirecting to ' + to.path) }
      setAccessToken(response.data.payload.accessToken)
      Vue.http.headers.common.Authorization = 'Bearer ' + getAccessToken()
      // refreshReportingHierarchy()
      next({
        path: to.path
      })
    }, error => {
      if (!isProductionEnvironment) { console.log(error) }

      if (error.status === 0) {
        EventBus.$emit('alert', {
          variant: 'error',
          message: i18n.t('apiGatewayStatusCodes.0'),
          errors: []
        })
      }

      next({
        path: '/'
      })
    })
}

export function hasPermissionChangeAuthority (claims) {
  // Allow always for admin
  if (hasAdminClaim()) { return true }

  // Allow operators to change all users - except admins
  if (hasUserManageClaim()) {
    if (claims.constructor === Array) {
      if (claims.indexOf(ADMIN_CLAIM) >= 0) {
        return false
      }
    } else if (claims.localeCompare(ADMIN_CLAIM) === 0) {
      return false
    }

    return true
  }

  return false
}

// Private methods

function hasClaim (claim) {
  const claims = securityStore.getters.claims
  if (claims.constructor === Array) {
    return claims.indexOf(claim) >= 0 || claims.indexOf(ADMIN_CLAIM) >= 0
  }

  return claims.localeCompare(claim) === 0 || claims.localeCompare(ADMIN_CLAIM) === 0
}

function getTokenExpirationDate (encodedToken) {
  var token = decode(encodedToken)
  if (!token.exp) { return null }

  var date = new Date(0)
  date.setUTCSeconds(token.exp)

  return date
}

function isTokenExpired (token) {
  var expirationDate = getTokenExpirationDate(token)
  return expirationDate < new Date()
}

function getTokenOrganizationId () {
  var token = getAccessToken()
  var decodedToken = decode(token)
  return decodedToken.user_organization_id
}

// check claim functions

export function hasAdminClaim () {
  return hasClaim(ADMIN_CLAIM)
}

export function hasUserManageClaim () {
  return hasClaim(CLAIMS[9])
}

export function hasUserReviewClaim () {
  return hasClaim(CLAIMS[4])
}

export function hasUserViewClaim () {
  return hasClaim(CLAIMS[8])
}

export function hasUserUpdateAllClaim () {
  return hasClaim(CLAIMS[5])
}

export function hasUserStatusUpdateClaim () {
  return hasClaim(CLAIMS[6])
}

export function hasUserStatusUpdateAllClaim () {
  return hasClaim(CLAIMS[7])
}

export function hasRoleViewClaim () {
  return hasClaim(CLAIMS[3])
}

export function hasRoleManageClaim () {
  return hasClaim(CLAIMS[2])
}

export function hasOrganizationViewClaim () {
  return hasClaim(CLAIMS[0])
}

export function hasOrganizationRegistrationResolveClaim () {
  return hasClaim(CLAIMS[1])
}

export function hasProjectViewClaim () {
  return hasClaim(CLAIMS[10])
}

export function hasProjectManageClaim () {
  return hasClaim(CLAIMS[11])
}

export function hasProjectEvaluateClaim () {
  return hasClaim(CLAIMS[12])
}

export function hasProjectListViewClaim () {
  return hasClaim(CLAIMS[13])
}

export function hasProjectListManageClaim () {
  return hasClaim(CLAIMS[14])
}

export function hasFormManageClaim () {
  return hasClaim(CLAIMS[15])
}

export function hasFormConfirmationResolveClaim () {
  return hasClaim(CLAIMS[16])
}

export function hasFormApprovalResolveClaim () {
  return hasClaim(CLAIMS[17])
}

export function hasProjectActionManageClaim () {
  return hasClaim(CLAIMS[11]) || hasClaim(CLAIMS[16]) || hasClaim(CLAIMS[17])
}

export function hasDMSManageClaim () {
  return hasClaim(CLAIMS[18])
}

export function hasOrganizationManageClaim () {
  return hasClaim(CLAIMS[19])
}

export function hasFormImportantManage () {
  return hasClaim(CLAIMS[20])
}

export function hasProjectIdeaVerificationManage () {
  return hasClaim(CLAIMS[21])
}

export function hasProjectIdeaApproveManage () {
  return hasClaim(CLAIMS[22])
}

export function hasMeiClaim () {
  const claims = securityStore.getters.claims
  if (claims.constructor === Array) {
    return claims.indexOf(CLAIMS[23]) >= 0
  }
  return claims.localeCompare(CLAIMS[23]) === 0
}

export function hasMinisterClaim () {
  const claims = securityStore.getters.claims
  if (claims.constructor === Array) {
    return claims.indexOf(CLAIMS[24]) >= 0
  }
  return claims.localeCompare(CLAIMS[24]) === 0
}

export function hasProjectSendMinistryClaim () {
  return hasClaim(CLAIMS[25])
}

export function hasProjectIdeaMinisterApprovalClaim () {
  return hasClaim(CLAIMS[26])
}

export function hasMeiApprovalCommentSendClaim () {
  return hasClaim(CLAIMS[27])
}

export function hasSendRequestChangeForm () {
  const claims = securityStore.getters.claims
  if (claims.constructor === Array) {
    return claims.indexOf(CLAIMS[28]) >= 0
  }
  return claims.localeCompare(CLAIMS[28]) === 0
}

export function getOrganizationId () {
  return getTokenOrganizationId()
}
